import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { DYNAMIC_COMPONENT_DATA } from '@principle-theorem/ng-shared';
import { TyroEasyclaimPayload } from '@principle-theorem/principle-core';
import {
  TransactionStatus,
  TransactionType,
  type ITyroBaseExtendedData,
} from '@principle-theorem/principle-core/interfaces';
import {
  TYRO_PENDING_VERIFICATION_STATUS_LABEL,
  TransactionCompleteResult,
  fromXMLPayload,
  isEasyclaimCompleteCallback,
  isHealthPointBaseTransactionCallbackData,
  isHealthPointTransactionErrorCallback,
} from '@principle-theorem/tyro';
import { type ITransactionExtendedDataDisplayData } from '../../../transactions-display/transaction-display/transaction-display-line/transaction-display-line.component';

@Component({
  selector: 'pr-tyro-extended-data',
  templateUrl: './tyro-extended-data.component.html',
  styleUrls: ['./tyro-extended-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TyroExtendedDataComponent {
  status?: string;

  constructor(
    @Inject(DYNAMIC_COMPONENT_DATA)
    public data: ITransactionExtendedDataDisplayData<ITyroBaseExtendedData>
  ) {
    this.status = this._getStatus(data.transaction.extendedData);
  }

  private _getStatus(data?: ITyroBaseExtendedData): string {
    if (!data) {
      return '';
    }
    if (isHealthPointBaseTransactionCallbackData(data.response)) {
      if (isHealthPointTransactionErrorCallback(data.response)) {
        return this._getErrorStatus();
      }

      // Approved claim later voided by staff. Tyro result still says APPROVED but status is Failed
      const isVoided =
        this.data.transaction.status === TransactionStatus.Failed &&
        data.response.result === TransactionCompleteResult.APPROVED;
      if (isVoided) {
        return TransactionCompleteResult.VOIDED;
      }

      return data.response.result;
    }

    if (isEasyclaimCompleteCallback(data.response)) {
      const payload = fromXMLPayload(data.response.payload);
      const errorCode = TyroEasyclaimPayload.getErrorCode(payload);
      return errorCode ? `ERROR CODE: ${errorCode}` : '';
    }
    return '';
  }

  private _getErrorStatus(): string {
    switch (this.data.transaction.status) {
      case TransactionStatus.Pending:
        return TYRO_PENDING_VERIFICATION_STATUS_LABEL;
      case TransactionStatus.Complete:
        return this.data.transaction.type === TransactionType.Outgoing
          ? TransactionCompleteResult.CANCELLED
          : TransactionCompleteResult.APPROVED;
      case TransactionStatus.Failed:
        return TransactionCompleteResult.VOIDED;
      default:
        return TransactionCompleteResult.UNKNOWN;
    }
  }
}
